/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Box, Grid, TextField, MenuItem, IconButton } from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CustomTextField, SelectDropdown } from '../../index';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ContainedButton } from '../../index';
import { useState } from 'react';

export const EducationalBackgroundTab = (props) => {
  const handleChange = (index, field, value) => {
    props.setEmpEducation((prev) => {
      const updated = [...prev];

      updated[index] = { ...updated[index], [field]: value };

      return updated;
    });
  };

  const handleAdd = () => {
    props.setEmpEducation((prev) => [
      {
        empid: props.empid,
        level: '',
        school: '',
        degree: '',
        gradyear: null,
        highestattained: '',
        startdate: null,
        enddate: null,
        honors: '',
      },
      ...prev,
    ]);
  };

  const handleDelete = (index) => {
    props.setEmpEducation((prev) => prev.filter((_, i) => i !== index));
  };

  const boxStyle = {
    mx: 0,
    my: 1,
    px: 3,
    py: 2,
  };
  const [startTouched, setStartTouched] = useState(false);

  return (
    <Box sx={{ ...boxStyle, maxHeight: '400px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {!props.viewOnly && (
          <ContainedButton
            startIcon={<Add />}
            onClick={handleAdd}
            variant="contained"
            btnTitle={'Add Education'}
            style={{ width: 'auto' }}
          />
        )}
      </Box>
      {props.empEducation.map((educ, index) => (
        <Box
          key={index}
          sx={{
            bgcolor: 'rgb(26, 32, 44, 0.6)',
            display: 'flex',
            justifyContent: 'start',
            p: 2,
            mt: 2,
            borderRadius: '5px',
            position: 'relative',
          }}
        >
          {!props.viewOnly && (
            <IconButton
              aria-label="delete"
              onClick={() => handleDelete(index)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                mr: 1,
              }}
            >
              <Close />
            </IconButton>
          )}
          <Grid container columnSpacing={2} sx={{ mt: 3 }}>
            <Grid item xs={6}>
              <CustomTextField
                error={!educ.level}
                errorMsg="Please enter an educational level"
                label="Educational Level"
                value={educ.level || ''}
                onChange={(e) => handleChange(index, 'level', e.target.value)}
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!educ.school}
                errorMsg="Please enter name of school"
                label="Name of School"
                value={educ.school || ''}
                onChange={(e) => handleChange(index, 'school', e.target.value)}
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter a degree"
                label="Degree"
                value={educ.degree || ''}
                onChange={(e) => handleChange(index, 'degree', e.target.value)}
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Year Graduated"
                  value={educ.gradyear ? new Date(educ.gradyear) : null}
                  onChange={(newValue) =>
                    handleChange(index, 'gradyear', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  maxDate={new Date()}
                  views={['year']} // Only allow selecting year
                  renderInput={(params) => (
                    <TextField
                      margin="normal"
                      fullWidth
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          cursor: props.viewOnly ? 'default' : undefined,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter highest attained"
                label="Highest Attained Year Level/Units/Grade"
                value={educ.highestattained || ''}
                onChange={(e) =>
                  handleChange(index, 'highestattained', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter a honors/awards received"
                label="Honors/Awards Received"
                value={educ.honors || ''}
                onChange={(e) => handleChange(index, 'honors', e.target.value)}
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={educ.startdate ? new Date(educ.startdate) : null}
                  maxDate={educ.enddate ? new Date(educ.enddate) : undefined}
                  onChange={(newValue) =>
                    handleChange(index, 'startdate', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  renderInput={(params) => {
                    const showError = startTouched && !educ.startdate;
                    return (
                      <TextField
                        onFocus={() => setStartTouched(true)}
                        margin="normal"
                        fullWidth
                        {...params}
                        error={showError}
                        helperText={showError ? 'Start Date is required' : ''}
                        inputProps={{
                          ...params.inputProps,
                          style: {
                            cursor: props.viewOnly ? 'default' : undefined,
                          },
                        }}
                        required
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={educ.enddate ? new Date(educ.enddate) : null}
                  minDate={
                    educ.startdate ? new Date(educ.startdate) : undefined
                  }
                  onChange={(newValue) =>
                    handleChange(index, 'enddate', newValue)
                  }
                  disableOpenPicker={props.viewOnly}
                  readOnly={props.viewOnly}
                  renderInput={(params) => (
                    <TextField
                      margin="normal"
                      fullWidth
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          cursor: props.viewOnly ? 'default' : undefined,
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
